import { ProductSubCategory } from '@getethos/ethos-global-lib';
import { isAdmin } from '../actionHelper';
import { Carriers } from '../constants/carriers';
import { DebitsValue, RateClass } from '../constants/decision';
import { Products } from '../constants/products';
import { RiderNames } from '../constants/rider';
import { UserOrganization } from '../constants/userOrganization';
import { EvidenceOrderProfile, ProductConfig } from './productConfig';

/**
 * Product configuration for North American IUL ACUM - Juvenile variant.
 *
 * Key differences from adult IUL in the workbench UI:
 * - Table ratings: Standard, Table 2 (B), Table 3 (C), Table 4 (D), Decline
 * - Debits: 0-49 (Standard), 50-74 (Table 2), 75-99 (Table 3), 100-124 (Table 4), 125+ (Decline)
 * - TRL not available
 * - Different product display text
 * - Different rider availability (no WMD, FDB)
 */
export class NorthAmericanNAIULACUMJuvenile extends ProductConfig {
  /**
   * Returns subCategory identifier for juvenile product variant.
   */
  getSubCategory(): string {
    return ProductSubCategory.JUVENILE;
  }
  supportsOwnerInsuredSplit(): boolean {
    return true;
  }
  shouldRenderOwnerOverview(): boolean {
    return true;
  }
  shouldRenderOwnerLinkedPolicies(): boolean {
    return true;
  }
  shouldSendLettersToOwner(): boolean {
    return true;
  }
  getEvidenceOrderProfile(_evidenceType?: string): EvidenceOrderProfile {
    return EvidenceOrderProfile.INSURED;
  }

  getCarrier(): Carriers {
    return Carriers.NORTH_AMERICAN;
  }

  getProduct(): Products {
    return Products.NAIULACUM;
  }

  availableToOrganization(org: UserOrganization): boolean {
    return [UserOrganization.NORTH_AMERICAN, UserOrganization.ETHOS].includes(
      org,
    );
  }

  getColor(): string {
    return 'Teal';
  }

  showPolicyCombined(roles: string[], org: string) {
    return (
      org === UserOrganization.NORTH_AMERICAN ||
      (isAdmin(roles) && org === UserOrganization.ETHOS)
    );
  }

  /**
   * Sahara Juvenile approvals are restricted to Standard — PreferredPlus and
   * Preferred are not offered for the juvenile variant on any decision type.
   */
  isRateClassAvailable(rateClass: string): boolean {
    return rateClass === RateClass.STANDARD;
  }

  /**
   * Show percentage addition for Standard rate class (same as adult Sahara).
   */
  showPercentageAddition(rateClass: string | undefined): boolean {
    return rateClass === RateClass.STANDARD;
  }

  /**
   * Show debits for Standard rate class.
   */
  showDebits(rateClass: string | undefined): boolean {
    return rateClass === RateClass.STANDARD;
  }

  /**
   * Juvenile supports percentage addition (table ratings).
   */
  hasPercentageAddition(): boolean {
    return true;
  }

  /**
   * Juvenile supports table ratings: Standard, Table 2-4, Decline.
   */
  hasTableRating(): boolean {
    return true;
  }

  /**
   * Juvenile supports debits.
   */
  hasDebits(): boolean {
    return true;
  }

  /**
   * Debits list for juvenile — one entry per table: Standard(0), T2(50), T3(75), T4(100).
   * No Debits_25 because juvenile has no Standard Plus (T1); debits 0-49 are all Standard.
   */
  getDebitsList(): DebitsValue[] {
    return [
      DebitsValue.Debits_0,
      DebitsValue.Debits_50,
      DebitsValue.Debits_75,
      DebitsValue.Debits_100,
    ];
  }

  /**
   * Calculate percentage addition from debits for juvenile.
   * Debits 0-49: PA 100-149 (Standard)
   * Debits 50-74: PA 150-174 (Table 2)
   * Debits 75-99: PA 175-199 (Table 3)
   * Debits 100-124: PA 200-224 (Table 4)
   * Debits 125+: Decline
   */
  getPa(debits: number): number {
    return debits + 100;
  }

  medicalExamAvailable(): boolean {
    return true;
  }

  ETIAvailable(): boolean {
    return true;
  }

  hasStandardPlusRating(_flags: any): boolean {
    return false;
  }

  /**
   * TRL is NOT available for juvenile per PRD.
   */
  TRLAvailable(): boolean {
    return false;
  }

  /**
   * Juvenile-specific riders - WMD and FDB not available (require age 18+).
   */
  getUnderWritableRiders(_state: string): string[] {
    return [
      RiderNames.RETURN_PREMIUM_DEATH_BENEFIT_ENDORSEMENT,
      RiderNames.ACCIDENTAL_DEATH_BENEFIT,
      RiderNames.ADBE_CHRONIC,
      RiderNames.ADBE_CRITICAL,
      // WMD not available for juvenile (requires age 18+)
      // FDB not available for juvenile (requires age 18+)

      // ADBE_TERMINAL is always available, it will be disabled in the UI
    ];
  }

  supportsFamilyCoverageDetails(): boolean {
    return true;
  }

  /**
   * Display text for juvenile product in workbench.
   */
  getProductDisplayText(): string {
    return `${this.getCarrier()} IUL JUVENILE`;
  }
}
